import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface InlineSelectMenuProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether the menu is visible */
  isOpen: boolean
  /** Max height before scrolling */
  maxHeight?: number
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled InlineSelectMenu component.
 *
 * Container for InlineSelectOption children. Only renders when isOpen is true.
 * Positioned below the trigger with compact styling.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const InlineSelectMenu = React.memo(
  ({
    isOpen,
    maxHeight = 200,
    children,
    style,
    className,
    ...props
  }: InlineSelectMenuProps) => {
    const [css, theme] = useStyletron()

    if (!isOpen) return null

    const menuClassName = css(getMenuStyles(theme, { maxHeight }))

    return (
      <div
        role="listbox"
        className={`${menuClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface MenuStyleParams {
  maxHeight: number
}

const getMenuStyles = (theme: any, { maxHeight }: MenuStyleParams) => ({
  position: 'absolute' as const,
  top: '100%',
  left: '0',
  marginTop: '4px',
  minWidth: '120px',
  backgroundColor: theme.colors.white,
  borderWidth: '1px',
  borderStyle: 'solid' as const,
  borderColor: theme.colors.grey20,
  borderRadius: theme.borders.radius200,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 100,
  overflowY: 'auto' as const,
  maxHeight: `${maxHeight}px`,
})

export default InlineSelectMenu
